<template>
  <div>
    <v-card outlined color="#f1f1f1" class="mb-5">
      <v-form>
        <ValidationObserver ref="form">
          <div class="d-flex flex-wrap align-center justify-space-between">
            <div class="d-flex flex-wrap mb-2">
              <v-chip label small>{{ claim.damage_type.name }}</v-chip>
              <template v-if="is_room">
                <v-chip class="ml-1" label color="primary" small>
                  <v-icon v-if="claim.room_feature.room_type" left small>
                    mdi-{{ claim.room_feature.room_type.icon }}
                  </v-icon>
                  {{ claim.room_feature.room_type_name }}
                </v-chip>
              </template>
              <template v-else-if="claim.product_feature">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      class="ml-1 mb-1"
                      label
                      color="primary"
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-shape</v-icon>
                      {{ claim.product_feature.local_category_name }}
                    </v-chip>
                  </template>
                  <span>{{
                    $capitalize($tc("model.damages_type_product"))
                  }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      class="ml-1 mb-1"
                      label
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ claim.product_feature.brand_name }}
                    </v-chip>
                  </template>
                  <span>{{ $capitalize($tc("model.damages_brand")) }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      class="ml-1 mb-1"
                      label
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ claim.product_feature.model }}
                    </v-chip>
                  </template>
                  <span>{{ $capitalize($tc("model.damages_model")) }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      class="ml-1 mb-1"
                      label
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon small left>mdi-barcode</v-icon>
                      {{ claim.product_feature.barcode }}
                    </v-chip>
                  </template>
                  <span>{{ $capitalize($tc("model.damages_bar_code")) }}</span>
                </v-tooltip>
              </template>
            </div>
            <div class="d-flex flex-wrap mb-2">
              <ScopeProvider scope="leads.damages.budgets.delete">
                <v-btn outlined icon @click="confirmDelete">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </ScopeProvider>
              <ScopeProvider scope="leads.damages.budgets.edit">
                <v-btn
                  outlined
                  icon
                  class="ml-2"
                  color="primary"
                  :loading="submitting"
                  @click="handleUpdate"
                >
                  <v-icon>mdi-content-save</v-icon>
                </v-btn>
              </ScopeProvider>
            </div>
          </div>
          <v-chip outlined label small color="primary">
            {{ claim.damage_name }}
          </v-chip>
          <v-card-actions
            class="py-0 d-flex flex-wrap justify-space-between align-center"
          >
            <ValidationProvider name="needs research" v-slot="{ errors }">
              <v-checkbox
                v-if="is_inconclusive || form.needs_research"
                v-model="form.needs_research"
                :label="$capitalize($tc('model.damages_needs_research'))"
                :error-messages="errors"
              />
              <v-spacer v-else />
            </ValidationProvider>
            <div class="d-flex flex-wrap">
              <v-chip class="mt-1 mr-1" v-if="claim.research_scheduled">
                <v-icon left>mdi-calendar-search</v-icon>
                {{ $capitalize($tc("model.damages_scheduled_search")) }}
              </v-chip>
              <v-chip class="mt-1 mr-1" @click="image_dialog = true">
                <v-icon left>
                  {{
                    claim.images.length > 0
                      ? "mdi-image-edit-outline"
                      : "mdi-image-plus"
                  }}
                </v-icon>
                {{ imagesInfo }}
              </v-chip>
              <v-chip class="mt-1 mr-1" @click="budget_dialog = true">
                <v-icon left>mdi-cash-usd</v-icon>
                {{ formatEuro(budget) }}
              </v-chip>
            </div>
          </v-card-actions>
        </ValidationObserver>
      </v-form>
    </v-card>
    <confirm-action-dialog
      :confirm_action="delete_dialog"
      :message="$capitalize($tc('model.damages_damage_deleted'))"
      @change="delete_dialog = $event"
      @action="handleDelete"
    />
    <v-dialog
      v-model="budget_dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-app-bar flat>
          <v-toolbar-title class="font-weight-medium">
            {{ $capitalize($tc("model.damages_budgets")) }}
          </v-toolbar-title>

          <v-spacer />
          <v-btn icon @click="budget_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-app-bar>
        <v-row style="max-width: 950px" class="mx-auto">
          <v-col cols="6">
            <v-file-input
              :label="
                $capitalize($tc('model.damages_external_budget_document'))
              "
              v-model="form.external_budget"
              show-size
              small-chips
              :clearable="false"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  @click="form.external_budget = null"
                  v-if="form.external_budget"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <template v-slot:selection="item">
                <v-chip>
                  <v-menu offset-overflow v-if="form.external_budget.id">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        :title="
                          $capitalize($tc('model.damages_document_preview'))
                        "
                        v-bind="attrs"
                        v-on="on"
                        icon
                        small
                        @click="external_budget_dialog = true"
                      >
                        <v-icon>mdi-file-eye-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-menu>
                  <span>{{ item.text }}</span>
                </v-chip>
              </template>
            </v-file-input>
          </v-col>
          <v-col cols="12">
            <RoomBudget
              v-if="is_room"
              :claim="claim"
              :coverages="available_coverages"
              @calcBudget="$listeners.calcBudget"
              @submit="handleBudgetUpdate"
            />
            <ProductBudget
              v-else
              :claim="claim"
              :coverages="available_coverages"
              @calcBudget="$listeners.calcBudget"
              @submit="handleBudgetUpdate"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="image_dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-app-bar flat>
          <v-toolbar-title class="font-weight-medium">
            {{ $capitalize($tc("model.damages_gallery")) }}
          </v-toolbar-title>

          <v-spacer />
          <v-btn icon @click="image_dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-app-bar>
        <v-row style="max-width: 950px" class="mx-auto">
          <v-col>
            <gallery-form :claim="claim" />
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <file-preview-dialog
      v-model="external_budget_dialog"
      @change="external_budget_dialog = $event"
      editable
      :file="form.external_budget"
      @handleChangedFile="handleChangeExternalBudget"
    />
  </div>
</template>

<script>
import RoomBudget from "@/components/Lead/RoomBudget";
import ProductBudget from "@/components/Lead/ProductBudget";
import GalleryForm from "@/components/forms/GalleryForm.vue";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import FilePreviewDialog from "@/components/FilePreviewDialog";
import ScopeProvider from "@/components/ScopeProvider";
import Money from "@/tools/money";
import { indexUsedCoverages } from "@/services/policy_coverage";
import {
  damage_classification,
  updateDamageClaim,
  destroyDamageClaim,
  uploadImage,
  deleteImage,
  linkAttachmentsToDamageClaim,
  patchExternalBudgetDoc,
  damage_types
} from "@/services/damages";

export default {
  name: "damage-claim",
  components: {
    RoomBudget,
    ProductBudget,
    GalleryForm,
    ConfirmActionDialog,
    FilePreviewDialog,
    ScopeProvider
  },
  props: {
    classification_style: {
      type: Object,
      default: () => ({})
    },
    claim: {
      type: Object,
      default: () => ({})
    },
    participant_type: String
  },
  data: () => ({
    budget_dialog: false,
    image_dialog: false,
    delete_dialog: false,
    external_budget_dialog: false,
    submitting: false,
    saving_external_budget: false,
    form: {
      room_feature: {
        room_type: {}
      },
      product_feature: {
        local_category: {}
      },
      damage: null,
      classification: null,
      needs_research: false
    }
  }),
  methods: {
    formatEuro(value) {
      return Money.euro(value ? value : 0);
    },
    async saveExternalBudget() {
      this.saving_external_budget = true;
      try {
        const response = await patchExternalBudgetDoc(
          this.$route.params["lead_number"],
          this.claim.id,
          this.form.external_budget
        );

        this.$store.dispatch("damage_claim/editDamageClaim", {
          ...this.claim,
          external_budget: response
        });
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: e.response.data.message,
          color: "red"
        });
      } finally {
        this.saving_external_budget = false;
      }
    },
    async handleChangeExternalBudget(external_budget) {
      this.$store.dispatch("damage_claim/editDamageClaim", {
        ...this.claim,
        external_budget
      });
    },
    async handleBudgetUpdate() {
      try {
        const response = await indexUsedCoverages(
          this.$route.params["lead_number"]
        );
        this.$store.commit("setLead", {
          ...this.lead,
          used_coverages: response
        });
      } catch {
        this.$store.commit("sendMessage", {
          text: "Não foi possível atualizar o consumo das coberturas",
          color: "red"
        });
      }
    },
    async handleUpdate() {
      this.submitting = true;
      try {
        const validation = await this.$refs.form.validate();
        if (!validation) return;

        const response = await updateDamageClaim(
          this.$route.params["lead_number"],
          this.claim.id,
          this.form
        );
        this.$store.dispatch("damage_claim/editDamageClaim", {
          ...this.form,
          ...response
        });
        this.$store.commit("sendMessage", {
          text: "Relatório de dano salvo com sucesso."
        });
      } catch (e) {
        this.$refs.form.setErrors(e.response.data.errors);
        this.$store.commit("sendMessage", {
          text: "Não foi possível excluir o dano. " + e.response.data.message,
          color: "red"
        });
      } finally {
        this.submitting = false;
      }
    },
    confirmDelete() {
      this.delete_dialog = true;
    },
    async handleDelete() {
      try {
        await destroyDamageClaim(
          this.$route.params["lead_number"],
          this.claim.id
        );
        this.$store.dispatch("damage_claim/removeDamageClaim", this.claim);
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: "Não foi possível excluir o dano. " + e.response.data.message,
          color: "red"
        });
      } finally {
        this.delete_dialog = false;
      }
    }
  },
  computed: {
    imagesInfo: function() {
      const length = this.claim.images.length;

      return length + (length == 1 ? " imagem" : " imagens");
    },
    budget: function() {
      return this.$listeners.calcBudget(this.claim);
    },
    classification: function() {
      if (this.form.classification == damage_classification.suitable)
        return this.classification_style["suitable"];

      if (this.form.classification == damage_classification.unsuitable)
        return this.classification_style["unsuitable"];

      if (this.form.classification == damage_classification.inconclusive)
        return this.classification_style["inconclusive"];

      return {};
    },
    is_room: function() {
      return this.claim.damage_type.id == damage_types.edificio;
    },
    is_inconclusive: function() {
      return this.form.classification == damage_classification.inconclusive;
    },
    lead: function() {
      return this.$store.getters.getLead;
    },
    classification_const: function() {
      return damage_classification;
    },
    breakpoint: function() {
      return this.$vuetify.breakpoint.name;
    },
    available_coverages: function() {
      return this.lead.policy.coverages.filter(coverage => {
        if (this.participant_type != "Segurado") return coverage.type == "RCP";
        return true;
      });
    }
  },
  created() {
    this.form = {
      ...this.form,
      ...this.claim
    };
  }
};
</script>

<style></style>
